import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  DollarSign,
  Sparkles,
  FlaskConical,
  ShieldCheck,
  Tag,
  AlertTriangle,
  FileText,
  MessageSquare,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import {
  NAVIGATION_CONFIG,
  NAVIGATION_GROUP_LABELS,
  NAVIGATION_GROUP_ORDER,
  NavigationGroup,
} from '../../types/navigation';

/**
 * Sidebar navigation.
 * Maps to Req 76 AC 2: role-based navigation
 * Maps to Req 166 AC 5: keyboard navigation
 * WCAG 2.1 AA compliant with proper navigation semantics
 *
 * Items are grouped into sections rather than listed flat. Ten destinations in
 * one undifferentiated column tells you nothing about which ones relate to
 * each other; three short labelled sets (Overview / Optimize / Manage) make
 * the app's shape legible at a glance. Groups come from NAVIGATION_CONFIG so
 * the sidebar and the header's page titles are driven by one definition.
 *
 * Collapsing to an icon rail is supported for narrow screens and for users who
 * want the horizontal room back on wide tables (Recommendations, Tagging).
 */
const NAV_ICONS: Record<string, React.ElementType> = {
  dashboard: LayoutDashboard,
  'cost-explorer': DollarSign,
  recommendations: Sparkles,
  'what-if-simulator': FlaskConical,
  policies: ShieldCheck,
  tagging: Tag,
  anomalies: AlertTriangle,
  invoice: FileText,
  'chat-history': MessageSquare,
  admin: Settings,
};

const COLLAPSE_STORAGE_KEY = 'sidebarCollapsed';

export const Sidebar: React.FC = () => {
  const { user } = useAuthStore();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
    } catch {
      // Private windows / blocked site data throw on access - a sidebar that
      // can't remember its state is fine, one that crashes the app is not.
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      } catch {
        /* ignore - see above */
      }
      return next;
    });
  };

  if (!user) return null;

  // Temporarily hidden from the sidebar for the demo, per explicit request -
  // the route/page itself is untouched, just not surfaced in nav yet.
  const HIDDEN_NAV_IDS = ['what-if-simulator'];

  const visibleNavItems = NAVIGATION_CONFIG.filter(
    (item) => item.roles.some((role) => user.roles.includes(role)) && !HIDDEN_NAV_IDS.includes(item.id)
  );

  // Only render a group heading if that group actually has visible items -
  // a Viewer sees no Admin Console, and an empty "Manage" label would be
  // a heading over nothing.
  const groups: Array<{ key: NavigationGroup; items: typeof visibleNavItems }> = NAVIGATION_GROUP_ORDER
    .map((key) => ({ key, items: visibleNavItems.filter((i) => i.group === key) }))
    .filter((g) => g.items.length > 0);

  const isItemActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <aside
      className={`flex flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      aria-label="Main navigation"
    >
      {/* Brand block. h-14 so it lines up exactly with the header beside it -
          the product identity belongs here, at the top of the persistent
          chrome, not in the header where it was competing with the page
          title for the same space. */}
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-sidebar-border px-4">
        <img
          src="/apexon_logo-removebg-preview.png"
          alt="CloudAlpha"
          className="h-9 w-9 shrink-0 object-contain"
        />
        {!collapsed && (
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-semibold tracking-tight text-primary">CloudAlpha</span>
            <span className="truncate text-2xs uppercase tracking-wide text-sidebar-foreground/50">
              Agentic Fin-Ops
            </span>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3" aria-label="Main navigation">
        {groups.map((group, groupIdx) => (
          <div key={group.key}>
            {groupIdx > 0 && <div className="mx-4 my-2 h-px bg-sidebar-border" />}
            {!collapsed && (
              <div className="mb-1 px-4 pt-1">
                <span className="text-2xs font-medium uppercase tracking-wider text-sidebar-foreground/40">
                  {NAVIGATION_GROUP_LABELS[group.key]}
                </span>
              </div>
            )}
            <ul className="flex flex-col gap-0.5 px-2" role="list">
              {group.items.map((item) => {
                const isActive = isItemActive(item.path);
                const Icon = NAV_ICONS[item.id];
                return (
                  <li key={item.id}>
                    <Link
                      to={item.path}
                      title={collapsed ? item.label : undefined}
                      className={`group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-ring ${
                        collapsed ? 'justify-center' : ''
                      } ${
                        isActive
                          ? 'bg-sidebar-accent/10 text-sidebar-accent'
                          : 'text-sidebar-foreground/60 hover:bg-sidebar-accent/5 hover:text-sidebar-foreground'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-sidebar-accent" />
                      )}
                      {Icon && (
                        <Icon
                          className={`h-4 w-4 shrink-0 transition-colors ${
                            isActive
                              ? 'text-sidebar-accent'
                              : 'text-sidebar-foreground/40 group-hover:text-sidebar-foreground/70'
                          }`}
                        />
                      )}
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border p-2">
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex w-full items-center justify-center rounded-md p-2 text-sidebar-foreground/40 transition-colors hover:bg-sidebar-accent/5 hover:text-sidebar-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );
};
